import { createFileRoute } from "@tanstack/react-router";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import {
  Mail,
  Search,
  Send,
  Eye,
  AlertTriangle,
  CheckCircle2,
  ArrowUp,
  ArrowDown,
  BarChart3,
  Copy,
} from "lucide-react";
import { HoverCard, HoverCardContent, HoverCardTrigger } from "@/components/ui/hover-card";
import { AdminShell } from "@/components/AdminShell";
import { DateCell } from "@/components/admin/DateCell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  listEmailTemplates,
  previewEmailTemplate,
  sendTestEmail,
  sendAllTestEmails,

  listEmailLog,
  getEmailStats,
  getEmailLogHtml,
  forwardEmailLog,
  getEmailSettings,
} from "@/lib/email.functions";
import { EmailEnvelope, type EmailEnvelopeData } from "@/components/admin/EmailEnvelope";
import { sendDailyReportNow } from "@/lib/daily-report.functions";

export const Route = createFileRoute("/_authenticated/admin/mejl")({
  head: () => ({
    meta: [
      { title: "Mejl — Administration | Sellavo" },
      { name: "description", content: "Alla mejltyper, testutskick och komplett mejllogg." },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: () => (
    <AdminShell title="Mejl">
      <EmailAdmin />
    </AdminShell>
  ),
});

function Stat({ label, value, tone }: { label: string; value: string | number; tone?: string }) {
  return (
    <div className="rounded-md border border-border bg-card p-4">
      <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{label}</p>
      <p className={`mt-2 font-heading text-2xl font-bold ${tone ?? ""}`}>{value}</p>
    </div>
  );
}

type SortKey = "created_at" | "template" | "to_email" | "subject" | "status";

function SortTh({
  label,
  col,
  sort,
  dir,
  onSort,
}: {
  label: string;
  col: SortKey;
  sort: SortKey;
  dir: "asc" | "desc";
  onSort: (c: SortKey) => void;
}) {
  const active = sort === col;
  return (
    <th className="whitespace-nowrap px-3 py-2">
      <button
        type="button"
        onClick={() => onSort(col)}
        className={`inline-flex items-center gap-1 uppercase tracking-wide transition-colors hover:text-foreground ${
          active ? "text-foreground" : ""
        }`}
      >
        {label}
        {active ? (
          dir === "asc" ? (
            <ArrowUp className="h-3 w-3" />
          ) : (
            <ArrowDown className="h-3 w-3" />
          )
        ) : null}
      </button>
    </th>
  );
}

function EmailHoverPreview({ id, subject }: { id: string; subject: string }) {
  const bodyFn = useServerFn(getEmailLogHtml);
  const q = useQuery({
    queryKey: ["email-body", id],
    queryFn: () => bodyFn({ data: { id } }),
    staleTime: 5 * 60_000,
  });
  return (
    <div className="overflow-hidden rounded-md">
      <div className="space-y-1 border-b border-border bg-muted/40 px-3 py-2">
        <p className="truncate text-[11px] text-muted-foreground">
          Från: {q.data?.from ?? "—"} · Till: {q.data?.to ?? "—"}
        </p>
        <p className="truncate text-xs font-semibold">{subject}</p>
        <p className="mt-0.5 text-[11px] text-muted-foreground">
          {q.isLoading ? "Hämtar mejlet…" : "Klicka på ämnet för att öppna i full storlek"}
        </p>
      </div>
      {q.data?.html ? (
        <iframe
          title="Mejlförhandsvisning"
          srcDoc={q.data.html}
          className="h-[360px] w-full bg-white"
        />
      ) : (
        <p className="px-3 py-6 text-center text-xs text-muted-foreground">
          {q.isLoading ? "Laddar…" : "Innehållet sparades inte för det här mejlet."}
        </p>
      )}
    </div>
  );
}

const TEST_ADDRESS = "sellavotest@beckman.club";

type TestResult = { key: string; label: string; ok: boolean; error?: string };

function EmailAdmin() {
  const templatesFn = useServerFn(listEmailTemplates);
  const previewFn = useServerFn(previewEmailTemplate);
  const testFn = useServerFn(sendTestEmail);
  const testAllFn = useServerFn(sendAllTestEmails);
  const logFn = useServerFn(listEmailLog);
  const bodyFn = useServerFn(getEmailLogHtml);
  const forwardFn = useServerFn(forwardEmailLog);
  const dailyFn = useServerFn(sendDailyReportNow);
  const statsFn = useServerFn(getEmailStats);

  const [search, setSearch] = useState("");
  const [template, setTemplate] = useState<string>("");
  const [status, setStatus] = useState<"" | "sent" | "failed">("");
  const [testTo, setTestTo] = useState(TEST_ADDRESS);
  const [allResults, setAllResults] = useState<TestResult[]>([]);
  const [preview, setPreview] = useState<{
    subject: string;
    html: string;
    id?: string;
    env?: EmailEnvelopeData;
  } | null>(
    null,
  );
  const [forwardTo, setForwardTo] = useState(TEST_ADDRESS);
  const [sort, setSort] = useState<SortKey>("created_at");
  const [dir, setDir] = useState<"asc" | "desc">("desc");


  const settingsFn = useServerFn(getEmailSettings);
  const settings = useQuery({ queryKey: ["email-settings"], queryFn: () => settingsFn({}) });
  const templates = useQuery({ queryKey: ["email-templates"], queryFn: () => templatesFn({}) });
  const stats = useQuery({ queryKey: ["email-stats"], queryFn: () => statsFn({}) });
  const log = useQuery({
    queryKey: ["email-log", search, template, status, sort, dir],
    queryFn: () =>
      logFn({
        data: {
          search: search || undefined,
          template: template || undefined,
          status: status || undefined,
          sort,
          dir,
          limit: 100,
        },
      }),
  });

  const doPreview = useMutation({
    mutationFn: (key: string) => previewFn({ data: { key } }),
    onSuccess: (d) => setPreview(d),
    onError: (e: Error) => toast.error(e.message),
  });

  const doTest = useMutation({
    mutationFn: (key: string) => testFn({ data: { key, to: testTo } }),
    onSuccess: () => {
      toast.success(`Testmejl skickat till ${testTo}`);
      stats.refetch();
      log.refetch();
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const doDaily = useMutation({
    mutationFn: () => dailyFn({ data: { to: testTo } }),
    onSuccess: (d) => {
      if (d.sent > 0) toast.success(`Dagsrapport med dagens riktiga siffror skickad till ${testTo}`);
      else toast.error("Dagsrapporten kunde inte skickas.");
      stats.refetch();
      log.refetch();
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const [bodyId, setBodyId] = useState<string | null>(null);

  const doForward = useMutation({
    mutationFn: () => forwardFn({ data: { id: preview!.id!, to: forwardTo } }),
    onSuccess: () => {
      toast.success(`Mejlet skickat till ${forwardTo}`);
      log.refetch();
      stats.refetch();
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const openBody = useMutation({
    mutationFn: (id: string) => {
      setBodyId(id);
      return bodyFn({ data: { id } });
    },
    onSuccess: (d, id) =>
      setPreview({
        id,
        subject: d.subject,
        env: d,
        html: d.html ?? "<p style=\"font:14px Helvetica,Arial;padding:16px\">Innehållet sparades inte för det här mejlet (skickat innan loggen började spara hela mejlet).</p>",
      }),
    onError: (e: Error) => toast.error(e.message),
  });

  const doTestAll = useMutation({
    mutationFn: () => testAllFn({ data: { to: testTo } }),
    onSuccess: (d) => {
      setAllResults(d.results);
      if (d.sent === d.total) toast.success(`Alla ${d.total} mejl skickade till ${testTo}`);
      else toast.warning(`${d.sent} av ${d.total} mejl skickades`);
      stats.refetch();
      log.refetch();
    },
    onError: (e: Error) => toast.error(e.message),
  });


  const toggleSort = (c: SortKey) => {
    if (c === sort) setDir(dir === "asc" ? "desc" : "asc");
    else {
      setSort(c);
      setDir(c === "created_at" ? "desc" : "asc");
    }
  };

  const s = stats.data;

  return (
    <div className="space-y-10">
      <section className="rounded-lg border border-border bg-card p-4">
        <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          Avsändare för alla mejl
        </p>
        <p className="mt-1 font-mono text-sm text-foreground">{settings.data?.from ?? "—"}</p>
        {settings.data?.debugMode ? (
          <p className="mt-1 text-xs text-muted-foreground">
            Förhandsläge: alla mejl utom testutskick styrs om till {settings.data.debugTo}.
          </p>
        ) : null}
      </section>

      <section className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
        <Stat label="Mejl totalt" value={s?.total ?? "–"} />
        <Stat label="Levererade" value={s?.sent ?? "–"} tone="text-primary" />
        <Stat label="Misslyckade" value={s?.failed ?? "–"} tone={s?.failed ? "text-destructive" : ""} />
        <Stat label="Senaste dygnet" value={s?.last24 ?? "–"} />
        <Stat label="Senaste 7 dagarna" value={s?.last7 ?? "–"} />
      </section>

      <section>
        <h2 className="font-heading text-xl font-bold">Alla mejl som kan skickas</h2>
        <p className="mt-1 text-sm text-muted-foreground">
          Förhandsgranska hur mejlet ser ut eller skicka ett skarpt testutskick.
        </p>
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <span className="text-sm text-muted-foreground">Testmejl till:</span>
          <Input
            value={testTo}
            onChange={(e) => setTestTo(e.target.value)}
            className="h-10 w-72"
            type="email"
          />
          <Button
            size="sm"
            variant="outline"
            onClick={() => setTestTo(TEST_ADDRESS)}
            disabled={testTo === TEST_ADDRESS}
          >
            Använd testadressen
          </Button>
          <Button size="sm" onClick={() => doTestAll.mutate()} disabled={doTestAll.isPending || !testTo}>
            <Send className="mr-1.5 h-4 w-4" />
            {doTestAll.isPending ? "Skickar alla…" : "Skicka alla mejl"}
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={() => doDaily.mutate()}
            disabled={doDaily.isPending || !testTo}
            title="Skickar dagsrapporten med dagens riktiga siffror i stället för exempeldata."
          >
            <BarChart3 className="mr-1.5 h-4 w-4" />
            {doDaily.isPending ? "Räknar och skickar…" : "Skicka dagsrapport (riktiga siffror)"}
          </Button>
        </div>
        {allResults.length > 0 && (
          <ul className="mt-3 space-y-1 rounded-md border border-border bg-card p-3 text-sm">
            {allResults.map((r) => (
              <li key={r.key} className="flex items-center gap-2">
                {r.ok ? (
                  <CheckCircle2 className="h-4 w-4 text-primary" />
                ) : (
                  <AlertTriangle className="h-4 w-4 text-destructive" />
                )}
                <span>{r.label}</span>
                {!r.ok && <span className="text-destructive">— {r.error}</span>}
              </li>
            ))}
          </ul>
        )}


        <div className="mt-4 grid gap-3 md:grid-cols-2">
          {(templates.data ?? []).map((t) => {
            const c = s?.byTemplate?.[t.key];
            return (
              <div key={t.key} className="rounded-lg border border-border bg-card p-4">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p className="font-semibold">{t.label}</p>
                    <p className="mt-1 text-sm text-muted-foreground">{t.description}</p>
                  </div>
                  <span className="shrink-0 rounded-full border border-border px-2 py-0.5 text-[11px] uppercase tracking-wide text-muted-foreground">
                    {t.source === "auth" ? "Inloggning" : "Sajten"}
                  </span>
                </div>
                <p className="mt-2 text-xs text-muted-foreground">
                  <strong>Skickas:</strong> {t.trigger}
                </p>
                <p className="mt-1 text-xs text-muted-foreground">
                  Skickade: {c?.sent ?? 0}
                  {c?.failed ? ` · ${c.failed} misslyckade` : ""}
                </p>
                <div className="mt-3 flex gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => doPreview.mutate(t.key)}
                    disabled={doPreview.isPending}
                  >
                    <Eye className="mr-1.5 h-4 w-4" /> Förhandsgranska
                  </Button>
                  <Button
                    size="sm"
                    onClick={() => doTest.mutate(t.key)}
                    disabled={doTest.isPending || !testTo}
                  >
                    <Send className="mr-1.5 h-4 w-4" /> Skicka test
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      <section>
        <h2 className="font-heading text-xl font-bold">Mejllogg</h2>
        <p className="mt-1 text-sm text-muted-foreground">
          Varje utskick loggas med mottagare, avsändare, typ, anledning och tidpunkt.
        </p>

        <div className="mt-3 flex flex-wrap items-center gap-2">
          <div className="relative">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Sök mottagare, ämne eller anledning"
              className="h-10 w-72 pl-8"
            />
          </div>
          <Select value={template || "all"} onValueChange={(v) => setTemplate(v === "all" ? "" : v)}>
            <SelectTrigger className="w-full sm:w-52" aria-label="Filtrera på typ">
              <SelectValue placeholder="Alla typer" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Alla typer</SelectItem>
              {(templates.data ?? []).map((t) => (
                <SelectItem key={t.key} value={t.key}>
                  {t.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select
            value={status || "all"}
            onValueChange={(v) => setStatus(v === "all" ? "" : (v as "sent" | "failed"))}
          >
            <SelectTrigger className="w-full sm:w-48" aria-label="Filtrera på status">
              <SelectValue placeholder="Alla statusar" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Alla statusar</SelectItem>
              <SelectItem value="sent">Levererade</SelectItem>
              <SelectItem value="failed">Misslyckade</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="mt-4 overflow-x-auto rounded-lg border border-border">
          <table className="w-full min-w-[820px] text-sm">
            <thead className="bg-muted/50 text-left text-xs uppercase tracking-wide text-muted-foreground">
              <tr>
                <SortTh label="Tid" col="created_at" sort={sort} dir={dir} onSort={toggleSort} />
                <SortTh label="Typ" col="template" sort={sort} dir={dir} onSort={toggleSort} />
                <th className="whitespace-nowrap px-3 py-2">Från</th>
                <SortTh label="Till" col="to_email" sort={sort} dir={dir} onSort={toggleSort} />
                <SortTh label="Ämne" col="subject" sort={sort} dir={dir} onSort={toggleSort} />
                <th className="whitespace-nowrap px-3 py-2">Anledning</th>
                <SortTh label="Status" col="status" sort={sort} dir={dir} onSort={toggleSort} />
              </tr>
            </thead>
            <tbody>
              {(log.data?.rows ?? []).map((r: any) => (
                <tr key={r.id} className="border-t border-border align-top">
                  <td className="whitespace-nowrap px-3 py-2">
                    <DateCell value={r.created_at} />
                  </td>
                  <td className="whitespace-nowrap px-3 py-2">{r.template}</td>
                  <td className="whitespace-nowrap px-3 py-2 text-muted-foreground">
                    {r.from_email ?? "—"}
                  </td>
                  <td className="whitespace-nowrap px-3 py-2">
                    {r.to_email}
                    {r.debug && r.actual_to !== r.to_email ? (
                      <span className="ml-1 text-xs text-muted-foreground">
                        (debug → {r.actual_to})
                      </span>
                    ) : null}
                  </td>
                  <td className="px-3 py-2">
                    <HoverCard openDelay={200} closeDelay={80}>
                      <HoverCardTrigger asChild>
                        <button
                          type="button"
                          onClick={() => openBody.mutate(r.id)}
                          className="text-left underline decoration-dotted underline-offset-4 hover:text-primary"
                        >
                          {r.subject}
                        </button>
                      </HoverCardTrigger>
                      <HoverCardContent className="w-[520px] p-0" align="start">
                        <EmailHoverPreview id={r.id} subject={r.subject} />
                      </HoverCardContent>
                    </HoverCard>
                  </td>
                  <td className="whitespace-nowrap px-3 py-2 text-muted-foreground">{r.reason}</td>
                  <td className="whitespace-nowrap px-3 py-2">
                    {r.status === "sent" ? (
                      <span className="inline-flex items-center gap-1 text-primary">
                        <CheckCircle2 className="h-4 w-4" /> Skickat
                      </span>
                    ) : (
                      <span
                        className="inline-flex items-center gap-1 text-destructive"
                        title={r.error ?? ""}
                      >
                        <AlertTriangle className="h-4 w-4" /> Fel
                      </span>
                    )}
                  </td>
                </tr>
              ))}
              {!log.isLoading && (log.data?.rows.length ?? 0) === 0 ? (
                <tr>
                  <td colSpan={7} className="px-3 py-8 text-center text-muted-foreground">
                    <Mail className="mx-auto mb-2 h-5 w-5" />
                    Inga mejl matchar filtret ännu.
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
        {log.data ? (
          <p className="mt-2 text-xs text-muted-foreground">
            Visar {log.data.rows.length} av {log.data.total} loggade mejl.
          </p>
        ) : null}
      </section>

      <Dialog open={!!preview} onOpenChange={(o) => !o && setPreview(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-base">{preview?.subject}</DialogTitle>
          </DialogHeader>
          {preview?.env ? (
            <EmailEnvelope data={preview.env} />
          ) : (
            <div className="rounded-md border border-border bg-muted/30 p-3 text-sm">
              <p className="text-xs uppercase tracking-wide text-muted-foreground">
                Mall-förhandsvisning
              </p>
              <p className="mt-1">
                Från: {settings.data?.from ?? "—"} · Ämne: <strong>{preview?.subject}</strong>
              </p>
            </div>
          )}
          <iframe
            title="Förhandsvisning"
            srcDoc={preview?.html ?? ""}
            className="h-[55vh] w-full rounded-md border border-border bg-white"
          />
          <div className="flex flex-col gap-2 border-t border-border pt-3 sm:flex-row sm:items-center">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={async () => {
                try {
                  await navigator.clipboard.writeText(preview?.html ?? "");
                  toast.success("Hela innehållet kopierat.");
                } catch {
                  toast.error("Kunde inte kopiera innehållet.");
                }
              }}
            >
              <Copy className="mr-2 h-4 w-4" /> Kopiera innehåll
            </Button>
            <div className="flex flex-1 gap-2">
              <Input
                type="email"
                value={forwardTo}
                onChange={(e) => setForwardTo(e.target.value)}
                placeholder="mottagare@exempel.se"
                className="h-10"
              />
              <Button
                type="button"
                size="sm"
                disabled={!preview?.id || !forwardTo || doForward.isPending}
                onClick={() => doForward.mutate()}
              >
                <Send className="mr-2 h-4 w-4" />
                {doForward.isPending ? "Skickar…" : "Skicka"}
              </Button>
            </div>
          </div>
          {!preview?.id ? (
            <p className="text-xs text-muted-foreground">
              Förhandsvisningar av mallar kan bara kopieras — använd testutskicken ovan för att
              skicka dem.
            </p>
          ) : null}
        </DialogContent>
      </Dialog>
    </div>
  );
}
